import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import {mergeMap, of, tap} from 'rxjs';
import {switchMap, map, catchError, withLatestFrom, takeUntil, filter} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
  addCadModel,
  addCadModelFailure,
  addCadModelRevision,
  addCadModelRevisionFailure,
  addCadModelRevisionSuccess,
  addCadModelSuccess,
  archiveCadModel,
  archiveCadModelFailure,
  archiveCadModelSuccess,
  downloadCadModelRevision,
  downloadCadModelRevisionFailure,
  downloadCadModelRevisionSuccess,
  generateIndentedBomReportCadModel,
  generateIndentedBomReportCadModelFailure,
  generateIndentedBomReportCadModelSuccess,
  generateIndentedBomReportCadModelRevision,
  generateIndentedBomReportCadModelRevisionFailure,
  generateIndentedBomReportCadModelRevisionSuccess,
  loadArchivedCadModels,
  loadArchivedCadModelsFailure,
  loadArchivedCadModelsSuccess,
  loadCadModels,
  loadCadModelsFailure,
  loadCadModelsSuccess,
  modifyCadModel,
  modifyCadModelFailure,
  modifyCadModelRevision,
  modifyCadModelRevisionFailure,
  modifyCadModelRevisionSuccess,
  modifyCadModelSuccess,
  removeCadModel,
  removeCadModelFailure,
  removeCadModelRevision,
  removeCadModelRevisionFailure,
  removeCadModelRevisionSuccess,
  removeCadModelSuccess,
  setSelectedCadModel,
  setSelectedCadModelFailure,
  setSelectedCadModelSuccess,
  unArchiveCadModel,
  unArchiveCadModelFailure,
  unArchiveCadModelSuccess,
  uploadCadModelRevision,
  uploadCadModelRevisionFailure,
  uploadCadModelRevisionSuccess,
  uploadNewlyAddedCadModelRevisionDrawingFailure,
  uploadNewlyAddedCadModelRevisionDrawingSuccess,
  uploadNewlyAddedCadModelRevisionFailure,
  uploadNewlyAddedCadModelRevisionSuccess,
  validateCadModelRevision,
  validateCadModelRevisionFailure,
  validateCadModelRevisionSuccess,
  verifyDeleteCadModelRevisionIsValid,
  verifyDeleteCadModelRevisionIsValidFailure,
  verifyDeleteCadModelRevisionIsValidSuccess,
} from "./cad-model.action";

import {selectAllCadModels, selectCadModels} from "./cad-model.selectors";
import {AppState} from "../../../shared/state/app.state";
import {CadModelService} from "../../data-access/cad-model.service";
import {Router} from "@angular/router";
import {ToastMessageOptions, MessageService} from "primeng/api";
import {GetMessage} from "../../../global-config/utils/toast-messages.utils";
import { ReportService } from "../../../report/data-access/report.service";

@Injectable()
export class CadModelEffects {

  loadCadModels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCadModels),
      switchMap(() =>
        this.cadModelService.get()
          .pipe(
            map((cadModels) => loadCadModelsSuccess({cadModels: cadModels})),
            catchError((error) => of(loadCadModelsFailure({ error: GetMessage('LoadFailure', 'Cad Models') })))
          )
      )
    )
  );

  routeToSelectedCadModel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSelectedCadModelSuccess),
      tap(action => {
        if (action?.selectedCadModel?.id){
          this.router.navigate(['/cad-data', action.selectedCadModel.id], {queryParamsHandling: 'preserve'});
        }
      })
    ), {dispatch: false}
  );

  setSelectedCadModelFromAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCadModelSuccess),
      switchMap((action) =>
        this.store.select(selectAllCadModels)
          .pipe(
            map((cadModels) => setSelectedCadModelSuccess({ selectedCadModel: cadModels.find(cadModel => cadModel.id == action.cadModel.id) ?? null })),
            catchError((error) => of(setSelectedCadModelFailure({ error })))
          )
      )
    )
  );

  modifyCadModel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(modifyCadModel),
      withLatestFrom(this.store.select(selectCadModels)),
      switchMap(([action, cadModels]) =>
        this.cadModelService.update(action.cadModel)
          .pipe(
            map((cadModel) => modifyCadModelSuccess({cadModel: cadModel, successMessage: GetMessage('ModifySuccess', 'Cad Model')})),
            catchError((error) => of(modifyCadModelFailure({ error: GetMessage('ModifyFailure', 'Cad Model') })))
          )
      )
    )
  );

  createCadModel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCadModel),
      withLatestFrom(this.store.select(selectCadModels)),
      switchMap(([action, cadModels]) =>
        this.cadModelService.create(action.cadModel)
          .pipe(
            map((addedCadModel) => addCadModelSuccess({cadModel: addedCadModel.data, successMessage: GetMessage('AddSuccess', 'Cad Model')})),
            catchError((error) => of(addCadModelFailure({ error: GetMessage('AddFailure', 'Cad Model') })))
          )
      )
    )
  );

  removeCadModel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeCadModel),
      withLatestFrom(this.store.select(selectCadModels)),
      switchMap(([action, cadModels]) =>
        this.cadModelService.remove(action.cadModelId)
          .pipe(
            map((removedCadModelId) => removeCadModelSuccess({cadModelId: removedCadModelId, successMessage: GetMessage('RemoveSuccess', 'Cad Model')})),
            catchError((error) => of(removeCadModelFailure({ error: GetMessage('RemoveFailure', 'Cad Model') })))
          )
      )
    )
  );

  modifyCadModelRevision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(modifyCadModelRevision),
      withLatestFrom(this.store.select(selectCadModels)),
      switchMap(([action, cadModels]) =>
        this.cadModelService.updateRevision(action.cadModelRevision)
          .pipe(
            map((cadModelRevision) => modifyCadModelRevisionSuccess({cadModelRevision: cadModelRevision, successMessage: GetMessage('ModifySuccess', 'Revision')})),
            catchError((error) => of(modifyCadModelRevisionFailure({ error: GetMessage('ModifyFailure', 'Revision') })))
          )
      )
    )
  );

  removeCadModelRevision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeCadModelRevision),
      withLatestFrom(this.store.select(selectCadModels)),
      switchMap(([action, cadModels]) =>
        this.cadModelService.removeRevision(action.cadModelRevisionId)
          .pipe(
            map((removedCadModelRevisionId) => removeCadModelRevisionSuccess({cadModelRevisionId: removedCadModelRevisionId, successMessage: GetMessage('RemoveSuccess', 'Revision')})),
            catchError((error) => of(removeCadModelRevisionFailure({ error: GetMessage('RemoveFailure', 'Revision') })))
          )
      )
    )
  );

  createCadModelRevision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCadModelRevision),
      switchMap((action) => {
          return this.cadModelService.createRevision(action.cadModelRevision)
            .pipe(
              map((addedCadModelRevision) => addCadModelRevisionSuccess({
                revisionFile: action.revisionFile,
                drawingFiles: action.drawingFiles,
                cadModelRevisionId: addedCadModelRevision.data.id,
                cadModelRevision: addedCadModelRevision.data,
                successMessage: GetMessage('AddSuccess', 'Revision')
              })),
              catchError((error) => of(addCadModelRevisionFailure({error: GetMessage('AddFailure', 'Revision')})))
            )
        }
      )
    )
  );

  uploadNewlyAddedCadModelRevision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCadModelRevisionSuccess),
      filter(action => action.revisionFile?.files),
      switchMap((action) => {
        return this.cadModelService.uploadRevision(action.revisionFile, action.cadModelRevisionId)
          .pipe(
            map((addedCadModelRevision) => uploadNewlyAddedCadModelRevisionSuccess({cadModelRevision: addedCadModelRevision.data, successMessage: GetMessage('UploadSuccess', 'Revision')})),
            catchError((error) => of(uploadNewlyAddedCadModelRevisionFailure({error: GetMessage('UploadFailure', 'Revision'), cadModelRevisionId: action.cadModelRevisionId})))
          );
      }
      )
    )
  );

  uploadNewlyAddedCadModelRevisionDrawings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addCadModelRevisionSuccess),
      filter(action => action.drawingFiles?.files),
      switchMap((action) => {
        return this.cadModelService.uploadDrawings(action.cadModelRevisionId, action.drawingFiles)
          .pipe(
            map((drawing) => uploadNewlyAddedCadModelRevisionDrawingSuccess({cadModelRevisionId: action.cadModelRevisionId, drawing: drawing.data, successMessage: GetMessage('UploadSuccess', 'Revision Drawing')})),
            catchError((error) => of(uploadNewlyAddedCadModelRevisionDrawingFailure({error: GetMessage('UploadFailure', 'Revision Drawing')})))
          );
        }
      )
    )
  );

  uploadCadModelRevision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadCadModelRevision),
      mergeMap((action) => {
        return this.cadModelService.uploadRevision(action.revisionFile, action.cadModelRevisionId)
          .pipe(
            map((addedCadModelRevision) => uploadCadModelRevisionSuccess({cadModelRevision: addedCadModelRevision.data, successMessage: GetMessage('UploadSuccess', 'Revision')})),
            catchError((error) => of(uploadCadModelRevisionFailure({error: GetMessage('UploadFailure', 'Revision'), cadModelRevisionId: action.cadModelRevisionId})))
          );
        }
      )
    )
  );

  downloadCadModelRevision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(downloadCadModelRevision),
      withLatestFrom(this.store.select(selectCadModels)),
      mergeMap(([action, cadModels]) => {
          return this.cadModelService.download(action.cadModelRevisionId, action.fileName)
            .pipe(
              map((file) => downloadCadModelRevisionSuccess({file: file, cadModelRevisionId: action.cadModelRevisionId})),
              catchError((error) => of(downloadCadModelRevisionFailure({error: GetMessage('DownloadFailure', 'Revision')})))
            )
        }
      )
    )
  );

  validateCadModelRevision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(validateCadModelRevision),
      withLatestFrom(this.store.select(selectCadModels)),
      mergeMap(([action, cadModels]) => {
          return this.cadModelService.validateRevision(action.cadModelRevisionId)
            .pipe(
              map((response) => {
                return validateCadModelRevisionSuccess({
                  cadModelRevision: response?.data
                })
              }),
              catchError((response) => of(validateCadModelRevisionFailure(
                {
                  cadModelRevision: response?.data
                }
              )))
            )
        }
      )
    )
  );

  verifyDeleteCadModelRevisionIsValid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyDeleteCadModelRevisionIsValid),
      switchMap((action) =>
        this.cadModelService.verifyDeleteRevisionIsValid(action.cadModelRevisionId)
          .pipe(
            map((verifyDeleteIsValidResponse) => verifyDeleteCadModelRevisionIsValidSuccess({verifyDeleteIsValidResponse})),
            catchError((error) => of(verifyDeleteCadModelRevisionIsValidFailure({ error: GetMessage('RemoveFailure', 'Cad Model Revision') })))
          )
      )
    )
  );

  toastSuccessMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          uploadCadModelRevisionSuccess,
          uploadNewlyAddedCadModelRevisionDrawingSuccess,
          uploadNewlyAddedCadModelRevisionSuccess,
          addCadModelRevisionSuccess,
          removeCadModelRevisionSuccess,
          modifyCadModelRevisionSuccess,
          removeCadModelSuccess,
          addCadModelSuccess,
          modifyCadModelSuccess,
          generateIndentedBomReportCadModelSuccess,
          generateIndentedBomReportCadModelRevisionSuccess
        ),
        tap(({ successMessage }) => {
            return this.messageService.add({
              severity: 'success',
              summary: successMessage,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );

  toastErrorMessages$ = createEffect(() =>
      this.actions$.pipe(
        ofType(
          downloadCadModelRevisionFailure,
          uploadCadModelRevisionFailure,
          uploadNewlyAddedCadModelRevisionDrawingFailure,
          uploadNewlyAddedCadModelRevisionFailure,
          addCadModelRevisionFailure,
          removeCadModelRevisionFailure,
          modifyCadModelRevisionFailure,
          removeCadModelFailure,
          addCadModelFailure,
          modifyCadModelFailure,
          loadCadModelsFailure,
          generateIndentedBomReportCadModelFailure,
          generateIndentedBomReportCadModelRevisionFailure
        ),
        tap(({ error }) => {
            return this.messageService.add({
              severity: 'error',
              summary: error,
            } as ToastMessageOptions)
          }
        )
      ),
    { dispatch: false }
  );

  generateIndentedBomReportCadModel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(generateIndentedBomReportCadModel),
      switchMap((action) =>
        this.cadModelService.generateIndentedBomReport(action.cadModelId)
          .pipe(
            map((response) => {
              // Extract the report from the response
              const report = response?.data;
              if (report?.id && report?.fileName) {
                // Trigger download
                this.reportService.download(report.id, report.fileName).subscribe();
              }
              return generateIndentedBomReportCadModelSuccess({
                cadModelId: action.cadModelId,
                successMessage: GetMessage('GenerateSuccess', 'Indented BOM Report')
              });
            }),
            catchError((error) => of(generateIndentedBomReportCadModelFailure({
              error: GetMessage('GenerateFailure', 'Indented BOM Report')
            })))
          )
      )
    )
  );

  generateIndentedBomReportCadModelRevision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(generateIndentedBomReportCadModelRevision),
      switchMap((action) =>
        this.cadModelService.generateIndentedBomReportRevision(action.cadModelRevisionId)
          .pipe(
            map((response) => {
              // Extract the report from the response
              const report = response?.data;
              if (report?.id && report?.fileName) {
                // Trigger download
                this.reportService.download(report.id, report.fileName).subscribe();
              }
              return generateIndentedBomReportCadModelRevisionSuccess({
                cadModelRevisionId: action.cadModelRevisionId,
                successMessage: GetMessage('GenerateSuccess', 'Indented BOM Report')
              });
            }),
            catchError((error) => of(generateIndentedBomReportCadModelRevisionFailure({
              cadModelRevisionId: action.cadModelRevisionId,
              error: GetMessage('GenerateFailure', 'Indented BOM Report')
            })))
          )
      )
    )
  );

  loadArchivedCadModels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadArchivedCadModels),
      switchMap(() =>
        this.cadModelService.getArchivedCadModels()
          .pipe(
            map((archivedCadModels) => loadArchivedCadModelsSuccess({archivedCadModels: archivedCadModels})),
            catchError((error) => of(loadArchivedCadModelsFailure({ error: GetMessage('LoadFailure', 'Archived CAD Models') })))
          )
      )
    )
  );

  archiveCadModel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(archiveCadModel),
      withLatestFrom(this.store.select(selectCadModels)),
      switchMap(([action, cadModels]) =>
        this.cadModelService.archive(action.cadModelId)
          .pipe(
            map((archivedCadModelId) => archiveCadModelSuccess({cadModelId: archivedCadModelId, successMessage: GetMessage('ArchiveSuccess', 'CAD Model')})),
            catchError((error) => of(archiveCadModelFailure({ error: GetMessage('ArchiveFailure', 'CAD Model')})))
          )
      )
    )
  );

  unArchiveCadModel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(unArchiveCadModel),
      withLatestFrom(this.store.select(selectCadModels)),
      switchMap(([action, cadModels]) =>
        this.cadModelService.unArchive(action.cadModelId)
          .pipe(
            map((archivedCadModelId) => unArchiveCadModelSuccess({cadModelId: archivedCadModelId, successMessage: GetMessage('UnArchiveSuccess', 'CAD Model')})),
            catchError((error) => of(unArchiveCadModelFailure({ error: GetMessage('UnArchiveFailure', 'CAD Model')})))
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private cadModelService: CadModelService,
    private reportService: ReportService,
    private router: Router,
    private messageService: MessageService,
  ) {}
}
